import axios, { AxiosError } from 'axios';
import { environmentConfig } from '../config/environment';
import { AICandidateData, AIInterviewResponse, AISessionInfo } from '../types/interview';

// 環境設定からAI面接エージェントのベースURLを取得
const AI_AGENT_API_URL = environmentConfig.aiAgentUrl;

// 後方互換性のためのエイリアス
export type CandidateData = AICandidateData;
export type SessionInfo = AISessionInfo;
export type { AIInterviewResponse };

// メッセージの型定義（AI面接システム用）
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * AI面接エージェントとの通信を管理するサービスクラス
 */
export class AIInterviewService {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL?: string, timeout: number = 30000) {
    this.baseURL = baseURL || AI_AGENT_API_URL;
    this.timeout = timeout;
  }

  /**
   * エラーハンドリングのヘルパー関数
   */
  private handleError(error: unknown, defaultMessage: string): AIInterviewResponse {
    console.error('AI Interview Service Error:', error);

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const responseData = axiosError.response?.data as any;
      return {
        success: false,
        error: responseData?.error || axiosError.message || defaultMessage,
      };
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : defaultMessage,
    };
  }

  /**
   * AI面接セッションを作成する
   */
  async createSession(
    candidateData: AICandidateData
  ): Promise<AIInterviewResponse<{ sessionId: string }>> {
    try {
      // interview-agentのAPI仕様に合わせてinputプロパティでラップ
      const requestBody = {
        input: candidateData,
      };

      const response = await axios.post(
        `${this.baseURL}/api/workflows/createSession/start-async`,
        requestBody,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: this.timeout,
        }
      );

      const result = response.data?.results?.createSession?.output;

      if (result?.success && result?.sessionId) {
        return {
          success: true,
          data: { sessionId: result.sessionId },
        };
      } else {
        console.error('❌ セッション作成失敗:', result);
        return {
          success: false,
          error: result?.error || result?.message || 'セッション作成に失敗しました',
        };
      }
    } catch (error) {
      return this.handleError(error, 'AI面接システムに接続できません');
    }
  }

  /**
   * AI面接セッションを開始する（初期挨拶メッセージを取得）
   */
  async startSession(sessionId: string): Promise<AIInterviewResponse<{ initialMessage: string }>> {
    try {
      const requestBody = {
        input: { sessionId },
      };

      const response = await axios.post(
        `${this.baseURL}/api/workflows/startSession/start-async`,
        requestBody,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: this.timeout,
        }
      );

      const result = response.data?.results?.startSession?.output;

      if (result?.success && result?.initialMessage) {
        return {
          success: true,
          data: { initialMessage: result.initialMessage },
        };
      } else {
        console.error('❌ セッション開始失敗:', result);
        return {
          success: false,
          error: `AI面接セッションの開始に失敗しました: ${result?.error || result?.message || 'レスポンス形式が不正です'}`,
        };
      }
    } catch (error) {
      console.error('AI面接セッション開始エラー:', error);
      return this.handleError(error, 'AI面接セッションの開始に失敗しました');
    }
  }

  /**
   * AI面接エージェントにメッセージを送信する
   */
  async sendMessage(
    sessionId: string,
    message: string,
    userId?: string,
    retryCount: number = 3
  ): Promise<AIInterviewResponse<{ agentMessage: string }>> {
    try {
      const requestBody = {
        input: {
          sessionId,
          message,
          userId,
        },
      };

      const response = await axios.post(
        `${this.baseURL}/api/workflows/sendMessage/start-async`,
        requestBody,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: this.timeout,
        }
      );

      const result = response.data?.results?.sendMessage?.output;

      if (result?.success && result?.agentMessage) {
        return {
          success: true,
          data: { agentMessage: result.agentMessage },
        };
      } else {
        console.error('❌ メッセージ送信失敗:', result);
        return {
          success: false,
          error: `AI面接エージェントから適切な応答が得られませんでした: ${result?.error || result?.message || 'レスポンス形式が不正です'}`,
        };
      }
    } catch (error) {
      console.error('AI面接メッセージ送信エラー:', error);

      // 再試行ロジック
      if (retryCount > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // 1秒待機
        return this.sendMessage(sessionId, message, userId, retryCount - 1);
      }

      // 最終的なエラーを返す
      return this.handleError(error, 'AI面接エージェントとの通信に失敗しました');
    }
  }

  /**
   * セッション一覧を取得する
   */
  async listSessions(
    limit: number = 50
  ): Promise<AIInterviewResponse<{ sessions: any[]; total: number }>> {
    try {
      const requestBody = {
        input: { limit },
      };

      const response = await axios.post(
        `${this.baseURL}/api/workflows/listSessions/start-async`,
        requestBody,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: this.timeout,
        }
      );

      const result = response.data?.results?.listSessions?.output;

      if (result?.success) {
        return {
          success: true,
          data: {
            sessions: result.sessions || [],
            total: result.total || 0,
          },
        };
      } else {
        console.error('❌ セッション一覧取得失敗:', result);
        return {
          success: false,
          error: result?.error || 'セッション一覧取得に失敗しました',
        };
      }
    } catch (error) {
      return this.handleError(error, 'AI面接システムに接続できません');
    }
  }

  /**
   * セッション詳細を取得する
   */
  async getSession(sessionId: string): Promise<AIInterviewResponse<AISessionInfo>> {
    try {
      const requestBody = {
        input: { sessionId },
      };

      const response = await axios.post(
        `${this.baseURL}/api/workflows/getSession/start-async`,
        requestBody,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: this.timeout,
        }
      );

      const result = response.data?.results?.getSession?.output;

      if (result?.success && result.session) {
        return {
          success: true,
          data: result.session,
        };
      } else {
        console.error('❌ セッション詳細取得失敗:', result);
        return {
          success: false,
          error: result?.error || 'セッション詳細取得に失敗しました',
        };
      }
    } catch (error) {
      return this.handleError(error, 'AI面接システムに接続できません');
    }
  }

  /**
   * セッション再開：過去の会話を注入して現フェーズから再開する
   */
  async resumeSession(params: {
    backendSessionId: string;
    currentPhase: 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING';
    existingMessages: Array<{ speaker: string; message: string }>;
    candidateData: {
      company_id?: string;
      company_name: string;
      industry: string;
      position: string;
      candidate_name: string;
      current_industry?: string;
      current_position?: string;
      is_experience?: boolean;
      candidate_type?: 'chuuto' | 'shinsotsu';
    };
  }): Promise<AIInterviewResponse<{ sessionId: string; openingMessage: string }>> {
    try {
      const response = await axios.post(
        `${this.baseURL}/api/workflows/resumeSession/start-async`,
        {
          backendSessionId: params.backendSessionId,
          currentPhase: params.currentPhase,
          existingMessages: params.existingMessages,
          params: params.candidateData,
        },
        { timeout: 60000 }
      );
      const result = response.data?.results?.resumeSession?.output;
      if (result?.success) {
        return { success: true, data: { sessionId: result.sessionId, openingMessage: result.openingMessage } };
      }
      return { success: false, error: result?.error || 'セッション再開に失敗しました' };
    } catch (error) {
      return this.handleError(error, 'セッション再開に失敗しました');
    }
  }

  /**
   * フェーズ遷移：エージェント側のセッションフェーズを更新し開始メッセージを返す
   */
  async advancePhase(
    sessionId: string,
    nextPhase: 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING'
  ): Promise<AIInterviewResponse<{ openingMessage: string; nextPhase: string }>> {
    try {
      const response = await axios.post(
        `${this.baseURL}/api/workflows/advancePhase/start-async`,
        { sessionId, nextPhase },
        { timeout: 30000 }
      );
      const result = response.data?.results?.advancePhase?.output;
      if (result?.success) {
        return {
          success: true,
          data: {
            openingMessage: result.openingMessage,
            nextPhase: result.nextPhase,
          },
        };
      }
      return { success: false, error: result?.error || 'フェーズ遷移に失敗しました' };
    } catch (error) {
      return this.handleError(error, 'フェーズ遷移に失敗しました');
    }
  }

  /**
   * AI面接システムの接続テスト
   */
  async testConnection(): Promise<AIInterviewResponse<{ status: string }>> {
    try {
      // Mastraサーバーのルートエンドポイントで接続テスト
      const response = await axios.get(`${this.baseURL}/`, {
        timeout: 5000,
      });

      if (response.status === 200) {
        return {
          success: true,
          data: { status: 'connected' },
        };
      } else {
        return {
          success: false,
          error: 'AI面接システムに接続できません',
        };
      }
    } catch (error) {
      return this.handleError(error, 'AI面接システムに接続できません');
    }
  }
}

// デフォルトのサービスインスタンスをエクスポート
export const aiInterviewService = new AIInterviewService();

// 後方互換性のために既存のAPI形式もエクスポート
export const aiInterviewApi = {
  createSession: (candidateData: AICandidateData) =>
    aiInterviewService.createSession(candidateData),
  startSession: (sessionId: string) => aiInterviewService.startSession(sessionId),
  sendMessage: (sessionId: string, message: string, userId?: string, retryCount?: number) =>
    aiInterviewService.sendMessage(sessionId, message, userId, retryCount),
  listSessions: (limit?: number) => aiInterviewService.listSessions(limit),
  getSession: (sessionId: string) => aiInterviewService.getSession(sessionId),
  testConnection: () => aiInterviewService.testConnection(),
  advancePhase: (sessionId: string, nextPhase: 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING') =>
    aiInterviewService.advancePhase(sessionId, nextPhase),
};
